import React from 'react';
import { Link } from 'react-router-dom';
import { Show } from '@refinedev/antd';
import { DateField } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, Avatar, Descriptions, Collapse, Space, theme, Button } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { UserDto, UserRole, UserStatus } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { UserOnboardingResponse } from './onboarding-response';
import { UserReferrals } from './referrals';
import { UserMemberTier } from './member-tier';
import { UserEfxMembership } from './efx-membership';
import { UserFeatureAccess } from './feature-access';

const roleColors: Record<UserRole, string> = {
  [UserRole.USER]: '#898989',
  [UserRole.MODERATOR]: '#3ecf8e',
  [UserRole.ADMIN]: '#f5a623',
  [UserRole.TRAINER]: '#1890ff',
};

const weekdayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** 1 = Monday … 7 = Sunday, matching ISO-8601. */
const weekdayName = (day?: number | null) =>
  day && day >= 1 && day <= 7 ? weekdayNames[day - 1] : '—';

const formatHourMinute = (hour?: number | null, minute?: number | null) =>
  hour == null ? '—' : `${String(hour).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;

const formatMealTimes = (times?: { breakfast?: string; lunch?: string; dinner?: string } | null) => {
  if (!times) return '—';
  const parts = [
    times.breakfast && `B ${times.breakfast}`,
    times.lunch && `L ${times.lunch}`,
    times.dinner && `D ${times.dinner}`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '—';
};

const statusColors: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: '#3ecf8e',
  [UserStatus.INACTIVE]: '#898989',
  [UserStatus.PENDING]: '#f5a623',
  [UserStatus.SUSPENDED]: '#ff6b6b',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const UserShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<UserDto>({ resource: 'users' });
  const user = queryResult?.data?.data;

  if (!user) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="users">
      <div style={{ padding: '8px 0' }}>
        {/* Avatar + Name header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <Avatar
            src={user.avatar}
            icon={!user.avatar && <UserOutlined />}
            size={64}
          />
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{user.fullName}</div>
            <div style={{ color: token.colorTextSecondary, fontSize: 13 }}>
              {user.email || user.phone || '—'}
            </div>
          </div>
        </div>

        <Field label="Email">{user.email || '—'}</Field>
        <Field label="Phone">{user.phone || '—'}</Field>
        <Field label="First Name">{user.firstName}</Field>
        <Field label="Last Name">{user.lastName}</Field>
        <Field label="User ID">{user.handle || '—'}</Field>
        <Field label="Friend Code">{user.friendCode || '—'}</Field>
        <Field label="Referral Code">{user.referralCode || '—'}</Field>
        <Field label="Role">
          <Tag style={tagStyle(roleColors[user.role])}>{user.role}</Tag>
        </Field>
        <Field label="Status">
          <Tag style={tagStyle(statusColors[user.status] || '#898989')}>{user.status}</Tag>
        </Field>
        <Field label="Onboarding Completed">{user.onboardingCompleted ? 'Yes' : 'No'}</Field>
        <Field label="EDM Opt-in">{user.edmOptIn ? 'Yes' : 'No'}</Field>
        <Field label="Terms Accepted">{user.termsAccepted ? 'Yes' : 'No'}</Field>
        <Field label="Last Login">
          {user.lastLoginAt ? <DateField value={user.lastLoginAt} /> : 'Never'}
        </Field>
        <Field label="Created At">
          <DateField value={user.createdAt} />
        </Field>
        {user.roles && user.roles.length > 0 && (
          <Field label="RBAC Roles">
            <Space size={4}>
              {user.roles.map((r) => (
                <Tag key={r.id}>{r.name}</Tag>
              ))}
            </Space>
          </Field>
        )}
      </div>

      {/* User Profile */}
      {user.userProfile && (
        <Collapse style={{ marginTop: 24 }} items={[{
          key: 'profile',
          label: 'User Profile',
          children: (
            <>
            <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.75 }}>
              Height, weight, gender, and DOB here come from onboarding Health Connect
              prefill (<code>updateSurveyProfile</code>), not directly from Health Sync
              Records. Use Health Data below for raw synced points and routed body metrics.
            </div>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Display Name">{user.userProfile.displayName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Gender">{user.userProfile.gender || '—'}</Descriptions.Item>
              <Descriptions.Item label="Partner">{user.userProfile.partnerName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Primary Goal">{user.userProfile.primaryGoal || '—'}</Descriptions.Item>
              <Descriptions.Item label="Activity Level">{user.userProfile.activityLevel || '—'}</Descriptions.Item>
              <Descriptions.Item label="Date of Birth">
                {user.userProfile.dateOfBirth
                  ? new Date(user.userProfile.dateOfBirth).toLocaleDateString()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Height">{user.userProfile.heightCm ? `${user.userProfile.heightCm} cm` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Current Weight">{user.userProfile.currentWeightKg ? `${user.userProfile.currentWeightKg} kg` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Starting Weight">{user.userProfile.startingWeightKg ? `${user.userProfile.startingWeightKg} kg` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Target Weight">{user.userProfile.targetWeightKg ? `${user.userProfile.targetWeightKg} kg` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Target Date">
                {user.userProfile.targetDate
                  ? new Date(user.userProfile.targetDate).toLocaleDateString()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Pace">
                {user.userProfile.paceKgPerWeek
                  ? `${user.userProfile.paceKgPerWeek} kg / week`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Fitness Level">{user.userProfile.fitnessExperienceLevel || '—'}</Descriptions.Item>
              <Descriptions.Item label="My Equipment">
                {user.userProfile.myEquipment && user.userProfile.myEquipment.length > 0
                  ? user.userProfile.myEquipment.join(', ')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Referral Code Entered">{user.userProfile.referralCode || '—'}</Descriptions.Item>
              <Descriptions.Item label="BMI">{user.userProfile.bmi ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Daily Calorie Target">{user.userProfile.dailyCalorieTarget ? `${user.userProfile.dailyCalorieTarget} kcal` : '—'}</Descriptions.Item>
              {user.userProfile.secondaryGoals && user.userProfile.secondaryGoals.length > 0 && (
                <Descriptions.Item label="Secondary Goals" span={2}>
                  {user.userProfile.secondaryGoals.join(', ')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Onboarding Completed">
                {user.userProfile.onboardingCompleted ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Onboarding Completed At">
                {user.userProfile.onboardingCompletedAt
                  ? new Date(user.userProfile.onboardingCompletedAt).toLocaleString()
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
            </>
          ),
        }]} />
      )}

      {user.userSettings && (
        <Collapse style={{ marginTop: 24 }} items={[{
          key: 'settings',
          label: 'App Settings',
          children: (
            <>
            <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.75 }}>
              What the user has set on the Profile tab. Read-only here: these are
              the user's own choices, and Biometric Lock in particular is enforced
              on their device, so switching it off from the CMS would not unlock
              anything for them.
            </div>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Language">{user.userSettings.language || '—'}</Descriptions.Item>
              <Descriptions.Item label="Biometric Lock">
                {user.userSettings.biometricLock ? 'On' : 'Off'}
              </Descriptions.Item>
              <Descriptions.Item label="Push Notifications">
                {user.userSettings.pushNotifications ? 'On' : 'Off'}
              </Descriptions.Item>
              <Descriptions.Item label="Achievement Alerts">
                {user.userSettings.achievementAlerts ? 'On' : 'Off'}
              </Descriptions.Item>
              <Descriptions.Item label="Meal Reminders">
                {user.userSettings.mealRemindersEnabled ? 'On' : 'Off'}
              </Descriptions.Item>
              <Descriptions.Item label="Meal Times">
                {formatMealTimes(user.userSettings.mealTimes)}
              </Descriptions.Item>
              <Descriptions.Item label="Bedtime Reminders">
                {user.userSettings.bedtimeRemindersEnabled ? 'On' : 'Off'}
              </Descriptions.Item>
              <Descriptions.Item label="Bedtime">
                {formatHourMinute(
                  user.userSettings.effectiveBedtimeHour,
                  user.userSettings.effectiveBedtimeMinute,
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Weight Reminders">
                {user.userSettings.weightRemindersEnabled ? 'On' : 'Off'}
              </Descriptions.Item>
              <Descriptions.Item label="Weight Reminder Schedule">
                {user.userSettings.weightRemindersEnabled
                  ? [
                      user.userSettings.weightReminderRepeat,
                      weekdayName(user.userSettings.weightReminderDayOfWeek),
                      formatHourMinute(
                        user.userSettings.weightReminderHour,
                        user.userSettings.weightReminderMinute,
                      ),
                    ]
                      .filter((part) => part && part !== '—')
                      .join(' · ') || '—'
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Timezone" span={2}>
                {user.userSettings.timezone || '—'}
              </Descriptions.Item>
            </Descriptions>
            </>
          ),
        }]} />
      )}

      <UserOnboardingResponse userId={user.id} />

      {user.planSelection && (
        <Collapse
          style={{ marginTop: 24 }}
          items={[{
            key: 'plan-selection',
            label: 'Paywall Selection',
            children: (
              <>
                <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.75 }}>
                  What the user selected on the onboarding paywall. It records the
                  choice, not the payment: a purchase is confirmed by the store and
                  currently lives only in RevenueCat, so this may show a plan the
                  user never completed buying — and it does not update when a
                  subscription later renews, lapses or is cancelled.
                </div>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Plan">
                    {user.planSelection.plan?.name ?? '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Billing Cycle">
                    {user.planSelection.billingCycle ?? '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Skipped">
                    {user.planSelection.skipped ? 'Yes' : 'No'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Chosen At">
                    {new Date(user.planSelection.createdAt).toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </>
            ),
          }]}
        />
      )}

      <UserMemberTier userId={user.id} />
      <UserEfxMembership userId={user.id} />
      <UserFeatureAccess userId={user.id} />
      <UserReferrals userId={user.id} />

      <Collapse
        style={{ marginTop: 24 }}
        items={[{
          key: 'health-data',
          label: 'Health Data',
          children: (
            <Space wrap>
              <Link to={`/health-sync-records?userId=${user.id}`}>
                <Button type="default">Health Sync Records</Button>
              </Link>
              <Link to={`/body-metric-logs?userId=${user.id}`}>
                <Button type="default">Body Metrics</Button>
              </Link>
            </Space>
          ),
        }]}
      />
    </Show>
  );
};
